import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  currentData: {
    user: null,
    suggestions: [],
    recipients: [],
  },
  isFetching: false,
  error: false,
};

const sortSuggestions = (items = []) => {
  return [...items].sort((a, b) => {
    const dateA = new Date(
      a?.last_message?.created_at || a?.updated_at || a?.created_at || 0
    ).getTime();

    const dateB = new Date(
      b?.last_message?.created_at || b?.updated_at || b?.created_at || 0
    ).getTime();

    return dateB - dateA;
  });
};

const mergeSuggestions = (current = [], incoming = []) => {
  const map = new Map();

  current.forEach((item) => {
    map.set(Number(item.id), item);
  });

  incoming.forEach((item) => {
    const key = Number(item.id);
    const existing = map.get(key);

    map.set(key, {
      ...existing,
      ...item,
      unread_count:
        typeof item?.unread_count !== "undefined"
          ? item.unread_count
          : existing?.unread_count || 0,
      last_message: item?.last_message || existing?.last_message || null,
    });
  });

  return sortSuggestions(Array.from(map.values()));
};

const suggestionsSlice = createSlice({
  name: "suggestions",
  initialState,
  reducers: {
    suggestionsStart: (state) => {
      state.isFetching = true;
      state.error = false;
    },

    suggestionsSuccess: (state, action) => {
      state.isFetching = false;
      const { user, suggestions } = action.payload || {};

      if (user) {
        state.currentData.user = user;
      }

      const incoming = Array.isArray(suggestions) ? suggestions : [];
      state.currentData.suggestions = mergeSuggestions(
        state.currentData.suggestions,
        incoming
      );
    },

    recipientsSuccess: (state, action) => {
      state.isFetching = false;
      const { recipients } = action.payload || {};
      state.currentData.recipients = Array.isArray(recipients)
        ? recipients
        : [];
    },

    suggestionsFailure: (state) => {
      state.isFetching = false;
      state.error = true;
    },

    addSuggestion: (state, action) => {
      state.isFetching = false;
      state.currentData.suggestions = mergeSuggestions(
        state.currentData.suggestions,
        [action.payload]
      );
    },

    updateSuggestion: (state, action) => {
      state.isFetching = false;
      state.currentData.suggestions = mergeSuggestions(
        state.currentData.suggestions,
        [action.payload]
      );
    },

    upsertSuggestionFromSocket: (state, action) => {
      state.currentData.suggestions = mergeSuggestions(
        state.currentData.suggestions,
        [action.payload]
      );
    },

    updateSuggestionUnread: (state, action) => {
      const {
        suggestionId,
        unread_count,
        incrementUnread,
        last_message,
        read_at,
        last_read_message_id,
      } = action.payload || {};

      const idx = state.currentData.suggestions.findIndex(
        (s) => Number(s.id) === Number(suggestionId)
      );

      if (idx === -1) return;

      const current = state.currentData.suggestions[idx];

      if (typeof unread_count !== "undefined") {
        current.unread_count = unread_count;
      } else if (incrementUnread) {
        current.unread_count = Number(current.unread_count || 0) + 1;
      }

      if (typeof last_message !== "undefined") {
        current.last_message = last_message;
      }

      if (typeof read_at !== "undefined") {
        current.read_at = read_at;
      }

      if (typeof last_read_message_id !== "undefined") {
        current.last_read_message_id = last_read_message_id;
      }

      state.currentData.suggestions = sortSuggestions(
        state.currentData.suggestions
      );
    },

    logoutSuggestions: (state) => {
      state.currentData = {
        user: null,
        suggestions: [],
        recipients: [],
      };
      state.isFetching = false;
      state.error = false;
    },
  },
});

export const {
  suggestionsStart,
  suggestionsSuccess,
  recipientsSuccess,
  suggestionsFailure,
  addSuggestion,
  updateSuggestion,
  upsertSuggestionFromSocket,
  updateSuggestionUnread,
  logoutSuggestions,
} = suggestionsSlice.actions;

export default suggestionsSlice.reducer;